import React from 'react';
import { cn } from '../../theme/utils';

/**
 * Base Skeleton Component
 * Provides a customizable skeleton loading animation
 */
const Skeleton = ({ 
  className = '',
  width = 'w-full',
  height = 'h-4',
  rounded = 'rounded',
  animate = true,
  children,
  ...props 
}) => {
  return (
    <div
      className={cn(
        'bg-neutral-800',
        animate && 'animate-pulse',
        width,
        height,
        rounded,
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Text Skeleton - for single line text
 */
export const SkeletonText = ({ lines = 1, className = '', ...props }) => {
  if (lines === 1) {
    return <Skeleton className={cn('h-4', className)} {...props} />;
  }

  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn(
            'h-4',
            index === lines - 1 && 'w-3/4', // Last line shorter
            className
          )}
          {...props}
        />
      ))}
    </div>
  );
};

/**
 * Avatar Skeleton - for profile pictures/avatars
 */
export const SkeletonAvatar = ({ size = 'md', className = '', ...props }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  return (
    <Skeleton
      className={cn('rounded-full', sizeClasses[size], className)}
      width=""
      height=""
      {...props}
    />
  );
};

/**
 * Button Skeleton - for button placeholders
 */
export const SkeletonButton = ({ size = 'md', variant = 'primary', className = '', ...props }) => {
  const sizeClasses = {
    sm: 'h-8 px-3',
    md: 'h-10 px-4',
    lg: 'h-12 px-6'
  };

  return (
    <Skeleton
      className={cn('rounded-lg', sizeClasses[size], className)}
      width=""
      height=""
      {...props}
    />
  );
};

/**
 * Card Skeleton - for card-based content
 */
export const SkeletonCard = ({ className = '', children, ...props }) => {
  return (
    <div className={cn('bg-neutral-900 border border-neutral-800 rounded-xl p-6', className)}>
      {children || (
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <SkeletonAvatar size="md" />
            <div className="flex-1 space-y-2">
              <SkeletonText className="h-5 w-1/2" />
              <SkeletonText className="h-4 w-3/4" />
            </div>
          </div>
          <SkeletonText lines={3} />
          <div className="flex space-x-2">
            <SkeletonButton size="sm" className="w-20" />
            <SkeletonButton size="sm" className="w-16" />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Table Row Skeleton - for table placeholders
 */
export const SkeletonTableRow = ({ columns = 4, className = '', ...props }) => {
  return (
    <tr className={cn('border-b border-neutral-800', className)}>
      {Array.from({ length: columns }).map((_, index) => (
        <td key={index} className="px-4 py-3">
          <SkeletonText className="h-4" />
        </td>
      ))}
    </tr>
  );
};

/**
 * Image Skeleton - for image placeholders
 */
export const SkeletonImage = ({ aspectRatio = 'aspect-video', className = '', ...props }) => {
  return (
    <Skeleton
      className={cn(aspectRatio, 'w-full', className)}
      width=""
      height=""
      {...props}
    />
  );
};

/**
 * List Item Skeleton - for list placeholders
 */
export const SkeletonListItem = ({ showAvatar = true, showMeta = true, className = '', ...props }) => {
  return (
    <div className={cn('flex items-center space-x-4 py-3', className)}>
      {showAvatar && <SkeletonAvatar size="md" />}
      <div className="flex-1 space-y-2">
        <SkeletonText className="h-5 w-3/4" />
        {showMeta && <SkeletonText className="h-4 w-1/2" />}
      </div>
      <SkeletonButton size="sm" className="w-16" />
    </div>
  );
};

/**
 * Form Field Skeleton - for form input placeholders
 */
export const SkeletonFormField = ({ withLabel = true, className = '', ...props }) => {
  return (
    <div className={cn('space-y-2', className)}>
      {withLabel && <SkeletonText className="h-4 w-24" />}
      <Skeleton className="h-10 rounded-lg" />
    </div>
  );
};

/**
 * Stats Skeleton - for statistics/metrics display
 */
export const SkeletonStats = ({ count = 3, className = '', ...props }) => {
  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-3 gap-4', className)}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="text-center space-y-2">
          <SkeletonText className="h-8 w-16 mx-auto" />
          <SkeletonText className="h-4 w-20 mx-auto" />
        </div>
      ))}
    </div>
  );
};

/**
 * Navigation Skeleton - for navigation menus
 */
export const SkeletonNavigation = ({ items = 5, className = '', ...props }) => {
  return (
    <nav className={cn('space-y-2', className)}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-center space-x-3 p-2">
          <Skeleton className="w-5 h-5 rounded" />
          <SkeletonText className="h-4 w-24" />
        </div>
      ))}
    </nav>
  );
};

/**
 * Loading Pulse - for content that's actively loading
 */
export const LoadingPulse = ({ className = '', children, ...props }) => {
  return (
    <div className={cn('animate-pulse', className)} {...props}>
      {children}
    </div>
  );
};

/**
 * Shimmer Effect - enhanced loading animation
 */
export const SkeletonShimmer = ({ className = '', children, ...props }) => {
  return (
    <div
      className={cn(
        'relative overflow-hidden bg-neutral-800 rounded',
        'before:absolute before:inset-0',
        'before:bg-gradient-to-r before:from-transparent before:via-neutral-700 before:to-transparent',
        'before:animate-[shimmer_2s_infinite]',
        className
      )}
      {...props}
    >
      {children}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

export default Skeleton;